import React, {
  FC,
  useState,
  useEffect,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import classnames from 'classnames';
import { Autocomplete, AutocompleteRenderOptionState } from '@material-ui/lab';
import { useField, useFormikContext, FormikValues } from 'formik';
import { CircularProgress, Chip } from '@material-ui/core';
import { TextField, Checkbox } from '@ccm-office/ccm-ui';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { Option } from 'types';
import { useStyles } from './MultiSelctField.styles';
import ClearIcon from '@material-ui/icons/Clear';
import { useTranslation } from 'react-i18next';
import PickerErrorText from '../../shared/PickerErrorText';

export interface IMultiSelectFieldProps {
  className?: string;
  name: string;
  label?: string;
  inputSize?: string;
  options: Array<Option>;
  getOptionLabel: (option: Option) => string;
  getOptionValue: (option: Option) => any;
  loading?: boolean;
  onChange?: (value: any | null) => void;
  required?: boolean;
  disabled?: boolean;
  limitTags?: number;
  placeholder?: string;
  error?: MultiSelectError;
}

interface MultiSelectError {
  showError: boolean;
  status?: number;
  message: string;
  labelField: string;
  valueField: string;
  setError?: (isInvalid: boolean) => void | Dispatch<SetStateAction<boolean>>;
}

interface LimitTagFnArgs {
  limitTags?: number;
  inputSize?: string;
}

const getLimitTagValue = ({ limitTags, inputSize }: LimitTagFnArgs): number => {
  if (Number.isInteger(limitTags)) {
    return Number(limitTags);
  }
  switch (inputSize) {
    case 'small':
      return 2;
    case 'medium':
      return 3;
    case 'large':
      return 4;
    default:
      return 2;
  }
};

const MultiSelectField: FC<IMultiSelectFieldProps> = ({
  name,
  label,
  placeholder,
  inputSize,
  options,
  getOptionLabel,
  getOptionValue,
  loading,
  required = false,
  disabled = false,
  limitTags,
  onChange,
  error,
}) => {
  const limitTag = useMemo(
    () => getLimitTagValue({ inputSize, limitTags }),
    [limitTags, inputSize]
  );
  const classes = useStyles({ inputSize, limitTag });
  const { t } = useTranslation();
  const { setFieldValue, submitCount } = useFormikContext<FormikValues>();
  const [field, meta, { setTouched }] = useField(name);
  const [multiAutocompleteValues, setMultiAutocompleteValues] = useState<Array<Option>>([]);
  const [isInvalidGroup, setIsInvalidGroup] = useState<boolean>(false);
  const status = error?.status;
  const shouldShowError = field?.value?.length > 0;
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  useEffect(() => {
    const values = field.value as unknown as Array<Option>;
    if (field.value && options.length && !error?.showError) {
      const selectedOptions = options.filter(
        (option) => values.indexOf(getOptionValue(option)) !== -1
      );
      setMultiAutocompleteValues(selectedOptions);
    } else if (
      field.value &&
      error?.showError &&
      !loading &&
      (options.length > 0 || error.status)
    ) {
      let isMissingGroup = false;
      const selectedOptions = values.map((val) => {
        const option = options.find((option) => getOptionValue(option) === val);
        if (!option) {
          isMissingGroup = true;
        }
        setIsInvalidGroup(isMissingGroup);
        if (error?.setError) {
          error.setError(isMissingGroup);
        }
        return option || { [error.labelField]: val, [error.valueField]: val, isInvalid: true };
      });
      setMultiAutocompleteValues(selectedOptions);
    }
  }, [field.value, getOptionValue, options, error?.showError, loading, error]);

  const handleChange = (_event: any, values: Array<Option>) => {
    const fieldValues = values.map((option) => getOptionValue(option));
    setFieldValue(name, fieldValues);
    if (onChange) {
      onChange(fieldValues);
    }
  };

  const onBlur = useCallback(() => {
    if (!meta.touched) setTouched(true);
  }, [meta.touched, setTouched]);

  const renderOption = (option: Option, state: AutocompleteRenderOptionState): React.ReactNode => {
    return (
      <React.Fragment key={getOptionValue(option)}>
        <Checkbox icon={icon} checkedIcon={checkedIcon} checked={state.selected} css="none" />
        {getOptionLabel(option)}
      </React.Fragment>
    );
  };
  const hasError = Boolean(meta.error && (meta.touched || submitCount > 0));
  const isError = error?.showError && (status || isInvalidGroup) && shouldShowError;
  return (
    <div
      className={classnames(classes.container, { [classes.errorBorder]: isError })}
      data-testid={`multi-picker-${name}-container`}
    >
      <Autocomplete
        multiple
        size="small"
        classes={{ option: classes.option, listbox: classes.listbox }}
        limitTags={limitTag}
        loading={loading}
        options={options}
        disableCloseOnSelect
        value={multiAutocompleteValues}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        filterOptions={createFilterOptions({
          matchFrom: 'any',
          ignoreCase: true,
          trim: true,
          limit: 50,
          stringify: (option: any) => getOptionLabel(option),
        })}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              deleteIcon={<ClearIcon />}
              label={getOptionLabel(option)}
              {...getTagProps({ index })}
              className={classnames(classes.tagSizeSmall, {
                [classes.errorChip]: option.isInvalid,
              })}
            />
          ))
        }
        disabled={disabled}
        onChange={handleChange}
        data-testid={`select-${name}`}
        noOptionsText={'No results found'}
        renderInput={(params) => {
          return (
            <TextField
              name={name}
              label={label}
              placeholder={placeholder}
              className={classes.textField}
              {...params}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off',
              }}
              InputProps={{
                ...params.InputProps,
                autoComplete: 'off',
                startAdornment: (
                  <React.Fragment>
                    <div className={classes.scroll}>{params.InputProps.startAdornment}</div>
                  </React.Fragment>
                ),
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              error={hasError}
              helperText={hasError && meta.error}
              variant="outlined"
              required={required}
              onBlur={onBlur}
            />
          );
        }}
      />
      <PickerErrorText
        isError={!!isError}
        isServiceError={!!(status && status >= 500)}
        serviceUnavailableMessage={t('error.catalogServiceUnavailable')}
        notFoundErrorMessage={error?.message}
      />
    </div>
  );
};

export default MultiSelectField;
